import Phases from '../../../../constants/model-phases'

export const normalizeFetchGradesPerComponentAnalyticsResponse = response => {
    const current = []
    const all = []

    response.layer_names.map((componentName, componentIndex) => {
        if (response.own.length) {
            const totalCurrentStudents = response.own[componentIndex]?.reduce((acc, curr) => acc + curr, 0) || 0
            const distributionOfCurrent = response.own[componentIndex].map((chunk, chunkIndex) => ({
                range:
                    response.bins[chunkIndex + 1] === undefined
                        ? `${response.bins[chunkIndex]}+`
                        : `${response.bins[chunkIndex]}-${response.bins[chunkIndex + 1]}`,
                numberOfStudents: chunk,
                percentOfStudents: totalCurrentStudents > 0 
                    ? Math.round((chunk / totalCurrentStudents) * 100) 
                    : 0,
            }))
            const averageGradeOfMyStudent = totalCurrentStudents > 0
                ? response.own[componentIndex].reduce((acc, curr, index) => acc + curr * response.bins[index], 0) / totalCurrentStudents
                : 0
            current.push({
                componentName,
                distribution: distributionOfCurrent,
                averageGrade: averageGradeOfMyStudent,
            })
        }

        const totalAllStudents = response.other[componentIndex]?.reduce((acc, curr) => acc + curr, 0) || 0
        const distributionOfAll = response.other[componentIndex].map((chunk, chunkIndex) => ({
            range:
                response.bins[chunkIndex + 1] === undefined
                    ? `${response.bins[chunkIndex]}+`
                    : `${response.bins[chunkIndex]}-${response.bins[chunkIndex + 1]}`,
            numberOfStudents: chunk,
            percentOfStudents: totalAllStudents > 0 
                ? Math.round((chunk / totalAllStudents) * 100) 
                : 0,
        }))
        const averageGradeOfStudent = totalAllStudents > 0
            ? response.other[componentIndex].reduce((acc, curr, index) => acc + curr * response.bins[index], 0) / totalAllStudents
            : 0
        all.push({
            componentName,
            distribution: distributionOfAll,
            averageGrade: averageGradeOfStudent,
        })
    })

    return {
        current,
        all,
        numberOfCurrentStudents: response.own[0]?.reduce((acc, curr) => acc + curr, 0) || 0,
        numberOfAllStudents: response.other[0]?.reduce((acc, curr) => acc + curr, 0) || 0,
    }
}

const normalizeStudentsGroupAttempts = studentsArray => {
    const totalStudents = studentsArray.reduce((acc, curr) => acc + curr, 0) || 0
    return studentsArray.map((numberOfStudents, index) => ({
        numberOfAttempts: index + 1,
        numberOfStudents,
        percentOfStudents: totalStudents > 0 
            ? Math.round((numberOfStudents / totalStudents) * 100)
            : 0,
    }))
}

export const normalizeFetchAttemptsAnalyticsResponse = response => ({
    current: normalizeStudentsGroupAttempts(response.own),
    all: normalizeStudentsGroupAttempts(response.other),
    numberOfCurrentStudents: response.own.reduce((acc, curr) => acc + curr, 0) || 0,
    numberOfAllStudents: response.other.reduce((acc, curr) => acc + curr, 0) || 0,
})

export const normalizeFetchGroupsAnalyticsResponse = response => {
    const normalizeData = dataType => {
        const MAX_GROUPS = 10

        const calculateTotalStudents = (data, index) => data[dataType][index]?.reduce((acc, curr) => acc + curr, 0) || 0

        const createGroupDetails = (clusterName, componentIndex, numberOfStudents, totalStudents) => ({
            name: clusterName,
            numberOfStudents,
            percentOfStudents: totalStudents > 0 
                ? Math.round((numberOfStudents / totalStudents) * 100) 
                : 0,
        })

        const sortByNumberOfStudents = (groupA, groupB) => groupB.numberOfStudents - groupA.numberOfStudents

        const groupExcessClustersAsOthers = groups => {
            const excessGroups = groups.slice(MAX_GROUPS)
            return excessGroups.reduce(
                (others, currentGroup) => {
                    others.numberOfStudents += currentGroup.numberOfStudents
                    others.percentOfStudents += currentGroup.percentOfStudents
                    return others
                },
                { name: 'Others', numberOfStudents: 0, percentOfStudents: 0 },
            )
        }

        return response.layers_info.map((component, componentIndex) => {
            const totalStudents = calculateTotalStudents(response, componentIndex)
            let groups = component.clusters.map((clusterName, clusterIndex) => {
                const numberOfStudents = response[dataType][componentIndex][clusterIndex]
                return createGroupDetails(clusterName, componentIndex, numberOfStudents, totalStudents)
            })

            groups = groups.filter(group => group.numberOfStudents > 0)
            groups.sort(sortByNumberOfStudents)

            if (groups.length > MAX_GROUPS) {
                const othersGroup = groupExcessClustersAsOthers(groups)
                groups = groups.slice(0, MAX_GROUPS)
                if (othersGroup.numberOfStudents > 0) groups.push(othersGroup)
            }

            return {
                componentName: component.name,
                groups,
            }
        })
    }

    return {
        current: response.own.length ? normalizeData('own') : [],
        all: normalizeData('other'),
        numberOfCurrentStudents: response.own[0]?.reduce((acc, curr) => acc + curr, 0) || 0,
        numberOfAllStudents: response.other[0]?.reduce((acc, curr) => acc + curr, 0) || 0,
    }
}

export const normalizeFetchUnitTestsAnalyticsResponse = response => {
    const normalizeData = type => {
        // Check if we have a valid total value that's not zero
        const total = response[type]?.total || 0
        
        return response.test_names.map((testName, testIndex) => {
            const numberOfStudentsPassedTheTest = response[type]?.passed[testIndex] || 0
            return {
                testName,
                numberOfStudentsPassedTheTest,
                percentOfStudentsPassedTheTest: total > 0 
                    ? Math.round((numberOfStudentsPassedTheTest / total) * 100)
                    : 0,
            }
        })
    }

    return {
        current: response.own ? normalizeData('own') : [],
        all: normalizeData('other'),
        numberOfCurrentStudents: response.own?.total || 0,
        numberOfAllStudents: response.other?.total || 0,
    }
}

export const normalizeFetchCategoriesDistributionByQuestionsAnalyticsResponse = response => {
    const totalReviewedSubmissions = response.questions[0].categories.reduce(
        (acc, curr) => acc + curr.number_of_submissions,
        0,
    )

    const questions = response.questions.map(question => {
        const categories = question.categories.map(category => ({
            id: category.id,
            numberOfSubmissions: category.number_of_submissions,
            percentFromAllSubmissions: Math.round((category.number_of_submissions / response.total_submissions) * 100),
            percentFromReviewedSubmissions: Math.round(
                (category.number_of_submissions / totalReviewedSubmissions) * 100,
            ),
        }))

        return {
            id: question.id,
            categories,
        }
    })

    return {
        questions,
        totalSubmissions: response.total_submissions,
        totalReviewedSubmissions,
        percentOfUnreviewedSubmissions: Math.round(
            ((response.total_submissions - totalReviewedSubmissions) / response.total_submissions) * 100,
        ),
        numberOfUnreviewedSubmissions: response.total_submissions - totalReviewedSubmissions,
    }
}

const phaseKeyToName = {
    predicted: Phases.PREDICTED,
    human: Phases.HUMAN_REVIEWED,
    imported: Phases.HISTORICAL,
}

export const normalizeFetchDistributionBetweenPhasesAnalyticsResponse = (response, meta, arg) => {
    const DIVERGENCE_THRESHOLD = 0.25 // Shannon-Jensen divergence threshold
    const totalSubmissions = response.total_submissions
    const phasesToShow = arg?.phasesToShow || Object.values(Phases)

    const processPhaseDistribution = (distribution, phaseName, totalSubmissions, categoryOrderMap = null) => {
        const totalReviewedSubmissions = distribution.reduce(
            (sum, { number_of_submissions }) => sum + number_of_submissions,
            0,
        )

        // Early return if there are no reviewed submissions
        if (totalReviewedSubmissions === 0) {
            return {
                name: phaseName,
                categories: distribution.map(({ id, number_of_submissions }) => ({
                    id,
                    numberOfSubmissions: number_of_submissions,
                    percentFromAllSubmissions: 0,
                    percentFromReviewedSubmissions: 0,
                })),
            }
        }

        const categories = distribution.map(({ id, number_of_submissions }) => {
            const percentFromAllSubmissions = totalSubmissions ? (number_of_submissions / totalSubmissions) * 100 : 0
            const exactPercentFromReviewed = (number_of_submissions / totalReviewedSubmissions) * 100
            const percentFloor = Math.floor(exactPercentFromReviewed)
            const remainder = exactPercentFromReviewed - percentFloor

            return {
                id,
                numberOfSubmissions: number_of_submissions,
                percentFromAllSubmissions: Math.round(percentFromAllSubmissions),
                percentFloor,
                remainder,
            }
        })

        // Sum of floored percentages
        let totalPercentFloor = categories.reduce((sum, { percentFloor }) => sum + percentFloor, 0)

        // Calculate how many percentage points are left to distribute
        let pointsToDistribute = 100 - totalPercentFloor

        // Sort categories by the largest remainder
        categories.sort((a, b) => b.remainder - a.remainder)

        // Distribute the remaining points based on the largest remainders
        categories.forEach(category => {
            if (pointsToDistribute > 0) {
                category.percentFromReviewedSubmissions = category.percentFloor + 1
                pointsToDistribute--
            } else {
                category.percentFromReviewedSubmissions = category.percentFloor
            }
            // Clean up temporary properties
            delete category.percentFloor
            delete category.remainder
        })

        // If includeUnreviewed is true, add unreviewed submissions
        if (arg?.includeUnreviewed) {
            const unreviewedSubmissionsNumber = totalSubmissions - totalReviewedSubmissions
            const percentFromAllSubmissions = totalSubmissions
                ? (unreviewedSubmissionsNumber / totalSubmissions) * 100
                : 0

            categories.push({
                id: null,
                numberOfSubmissions: unreviewedSubmissionsNumber,
                percentFromAllSubmissions: Math.round(percentFromAllSubmissions),
                percentFromReviewedSubmissions: 0,
            })
        }

        // Sort categories based on the provided order map or by numberOfSubmissions for the first phase
        if (categoryOrderMap) {
            // Sort according to the order established in the first phase
            categories.sort((a, b) => {
                if (a.id === null) return 1
                if (b.id === null) return -1
                return categoryOrderMap.get(a.id) - categoryOrderMap.get(b.id)
            })
        } else {
            // For the first phase, sort by numberOfSubmissions
            categories.sort((a, b) => {
                if (a.id === null) return 1
                if (b.id === null) return -1
                return b.numberOfSubmissions - a.numberOfSubmissions
            })
        }

        return { name: phaseName, categories }
    }

    const extractPhasesFromQuestion = (question, totalSubmissions) => {
        const phaseProperties = Object.keys(question).filter(propertyName =>
            propertyName.endsWith('_pattern_distribution'),
        )

        // Process first phase separately to establish category order
        const firstPhaseProperty = phaseProperties[0]
        const firstPhaseKey = firstPhaseProperty.split('_')[0]
        const firstPhaseName = phaseKeyToName[firstPhaseKey]
        const firstPhase = processPhaseDistribution(question[firstPhaseProperty], firstPhaseName, totalSubmissions)

        // Create category order map from the first phase
        const categoryOrderMap = new Map(
            firstPhase.categories
                .filter(category => category.id !== null)
                .map((category, index) => [category.id, index]),
        )

        // Process remaining phases using the established order
        const remainingPhases = phaseProperties.slice(1).map(propertyName => {
            const phaseKey = propertyName.split('_')[0]
            const phaseName = phaseKeyToName[phaseKey]
            return processPhaseDistribution(question[propertyName], phaseName, totalSubmissions, categoryOrderMap)
        })

        // Combine all phases and sort them by phase order
        return [firstPhase, ...remainingPhases].sort(
            (a, b) => Object.values(Phases).indexOf(a.name) - Object.values(Phases).indexOf(b.name),
        )
    }

    const calculateDivergenceBetweenPhases = (phaseA, phaseB, allCategoryIds) => {
        const getCategoryPercent = (phase, categoryId) => {
            const category = phase.categories.find(c => c.id === categoryId)
            return category ? category.percentFromReviewedSubmissions / 100 : 0 // Convert to probability (0-1)
        }

        // Calculate Shannon-Jensen divergence
        const divergenceSum = [...allCategoryIds].reduce((sum, categoryId) => {
            const pA = getCategoryPercent(phaseA, categoryId)
            const pB = getCategoryPercent(phaseB, categoryId)

            // Handle edge cases to avoid log(0)
            if (pA === 0 && pB === 0) return sum

            const m = (pA + pB) / 2
            const termA = pA === 0 ? 0 : pA * Math.log2(pA / m)
            const termB = pB === 0 ? 0 : pB * Math.log2(pB / m)

            return sum + (termA + termB) / 2
        }, 0)

        return divergenceSum
    }

    const calculateMaxDivergence = (phases, allCategoryIds) => {
        // Only consider phases that are in the phasesToShow array
        const filteredPhases = phases.filter(phase => phasesToShow.includes(phase.name))
        
        // Filter out phases with no submissions
        const activePhasesWithSubmissions = filteredPhases.filter(phase =>
            phase.categories.some(category => category.numberOfSubmissions > 0),
        )

        // If there are less than 2 active phases, there's no divergence to calculate
        if (activePhasesWithSubmissions.length < 2) {
            return 0
        }

        let maxDivergence = 0

        for (let i = 0; i < activePhasesWithSubmissions.length; i++) {
            for (let j = i + 1; j < activePhasesWithSubmissions.length; j++) {
                const divergence = calculateDivergenceBetweenPhases(
                    activePhasesWithSubmissions[i],
                    activePhasesWithSubmissions[j],
                    allCategoryIds,
                )
                if (divergence > maxDivergence) {
                    maxDivergence = divergence
                }
            }
        }

        return maxDivergence
    }

    const questions = response.questions.map(question => {
        const phases = extractPhasesFromQuestion(question, totalSubmissions)
        const allCategoryIds = new Set(
            phases.flatMap(phase =>
                phase.categories
                    .filter(category => category.id !== null) // Exclude unreviewed category
                    .map(category => category.id),
            ),
        )
        const maxDivergence = calculateMaxDivergence(phases, allCategoryIds)
        const isHighDivergent = maxDivergence > DIVERGENCE_THRESHOLD

        return { id: question.id, phases, isHighDivergent }
    })

    return { questions, totalSubmissions }
}

export const normalizeFetchConfidenceDistributionAnalyticsResponse = response => {
    const questions = response.questions.map(question => {
        const totalSubmissionsPerQuestion = question.confidence_distribution.reduce(
            (acc, curr) => acc + curr.number_of_submissions,
            0,
        )

        // Early return if there are no submissions for the question
        if (totalSubmissionsPerQuestion === 0) {
            const confidenceLevels = question.confidence_distribution.map(level => ({
                level: level.level,
                numberOfSubmissions: level.number_of_submissions,
                percentFromQuestionSubmissions: 0,
                percentFromAllSubmissions: 0,
            }))
            return {
                id: question.id,
                confidenceLevels,
                totalSubmissionsPerQuestion,
            }
        }

        // Calculate exact percentages and remainders
        const confidenceLevels = question.confidence_distribution.map(level => {
            const percentFromQuestionExact = (level.number_of_submissions / totalSubmissionsPerQuestion) * 100
            const percentFloor = Math.floor(percentFromQuestionExact)
            const remainder = percentFromQuestionExact - percentFloor

            const percentFromAllExact = (level.number_of_submissions / response.total_submissions) * 100
            const percentFromAllSubmissions = Math.round(percentFromAllExact)

            return {
                level: level.level,
                numberOfSubmissions: level.number_of_submissions,
                percentFloor,
                remainder,
                percentFromAllSubmissions,
            }
        })

        // Sum of floored percentages
        const totalPercentFloor = confidenceLevels.reduce((sum, level) => sum + level.percentFloor, 0)

        // Calculate how many percentage points are left to distribute
        let pointsToDistribute = 100 - totalPercentFloor

        // Sort confidence levels by the largest remainder
        confidenceLevels.sort((a, b) => b.remainder - a.remainder)

        // Distribute the remaining points based on the largest remainders
        confidenceLevels.forEach(level => {
            if (pointsToDistribute > 0) {
                level.percentFromQuestionSubmissions = level.percentFloor + 1
                pointsToDistribute--
            } else {
                level.percentFromQuestionSubmissions = level.percentFloor
            }
            // Clean up temporary properties
            delete level.percentFloor
            delete level.remainder
        })

        // Sort confidence levels back to their original order
        confidenceLevels.sort((a, b) => a.level - b.level)

        return {
            id: question.id,
            confidenceLevels,
            totalSubmissionsPerQuestion,
        }
    })

    return {
        questions,
        totalSubmissions: response.total_submissions,
    }
}

export const normalizeFetchQuestionsApproveAnalyticsResponse = response => {
    const questions = {}
    Object.keys(response).map(questionId => {
        questions[questionId] = {
            reviewedByAi: parseFloat(response[questionId].by_ai.toFixed(1)),
            reviewedByHuman: parseFloat(response[questionId].by_human.toFixed(1)),
            unreviewed: parseFloat(response[questionId].not_approve.toFixed(1)),
            totalSubmissions: response[questionId].total_submissions,
        }
    })
    return questions
}

export const normalizeFetchReviewersApproveAnalyticsResponse = (response) => {
    // Helper function to calculate percentage
    const calculatePercentCompleted = (reviewedSubmissions, assignedSubmissions) => {
        if (!assignedSubmissions || assignedSubmissions === 0) return 0
        return Math.round((reviewedSubmissions / assignedSubmissions) * 100)
    }
    
    // Real data transformation from backend
    const totalReviewedSubmissions = response.total_reviewed_submissions
    const totalAssignedSubmissions = response.total_assigned_submissions
    
    const reviewers = response.reviewers.map(reviewer => {
        const reviewedSubmissions = reviewer.reviewed_submissions
        const assignedSubmissions = reviewer.assigned_submissions
        
        return {
            userId: reviewer.user_id,
            reviewedSubmissions,
            assignedSubmissions,
            percentCompleted: calculatePercentCompleted(reviewedSubmissions, assignedSubmissions)
        }
    })
    
    return {
        reviewers,
        totalReviewedSubmissions,
        totalAssignedSubmissions,
        totalSubmissions: response.total_submissions,
        totalPercentCompleted: calculatePercentCompleted(totalReviewedSubmissions, totalAssignedSubmissions)
    }
}
